import { useMemo } from 'react';
import useEnabledProjectFields from './useEnabledProjectFields';
import { isFieldSettingEnabled } from "../utils/fieldSettings";

// This hook applies some additional rules to useEnabledProjectFields
// 1) It adds the hardcoded PDF fields listed below 
// 2) It filters out the asset fields listed from the exclusion lists below
// 3) It prevents the user from removing the mandatory fields listed below

// Hardcoded PDF-specific fields by type
const SHIPMENT_PDF_HARDCODED_FIELDS = [
  ["s_i_quantity", "shipment_items", "Quantity Received"],
  ["s_i_item_notes", "shipment_items", "Item Notes"], 
  ["s_i_total_pieces", "shipment_items", "Handling Units"],
];

const INVENTORY_REQUEST_PDF_HARDCODED_FIELDS = [
  ["i_i_quantity", "inventory_items", "Quantity Requested"],
  ["i_i_quantity_loaded", "inventory_items", "Quantity Loaded"],
  ["i_i_quantity_delivered", "inventory_items", "Quantity Delivered"],
  ["i_i_rack_location_changes", "inventory_items", "Pick Location(s)"],
  ["line_item", "inventory_items", "Line Item"],
];

const SHIPMENT_ITEM_PDF_HARDCODED_FIELDS = [
  ["s_i_quantity", "shipment_items", "Quantity Received"],
  ["s_i_item_notes", "shipment_items", "Item Notes"], 
  ["s_i_total_pieces", "shipment_items", "Handling Units"],
  ["s_i_rack_locations", "shipment_items", "Inbound Location"],
  ["s_i_mobile_location", "shipment_items", "Mobile Location"],
  ["truck_number", "shipment_items", "Truck Number"],
  ["bol", "shipment_items", "BOL"],
  ["carrier_name", "shipment_items", "Carrier Name"],
  ["customer_po_number", "shipment_items", "Shipment PO Number"],
  ["order_number", "shipment_items", "Order Number"],
  ["tracking_number", "shipment_items", "Tracking Number"],
  ["vendor_name", "shipment_items", "Vendor Name"],
];

const SHIPMENT_ITEM_MOVE_LABEL_HARDCODED_FIELDS = [
 // TODO: Add hardcoded fields for move label PDF if needed
];

// Mandatory fields that cannot be removed from UI
const MANDATORY_FIELDS = {
  shipment: [
    "item_id",
    "description",
    "s_i_quantity",
  ],
  
  inventory_request: [
    "item_id",
    "description",
    "i_i_quantity",
    "i_i_rack_location_changes"
  ],
  
  shipment_item: [
    "s_i_quantity"
  ],

  shipment_item_move_label: [
    "item_id",
    "description",
  ],
};

// Fields to exclude from Shipment PDF (Inbound Receiving Report)
const SHIPMENT_PDF_EXCLUDED_FIELDS = [
  "created_at",
  "updated_at", 
  "last_in",
  "last_out",
  "allocated_quantity",
  "in_cart_quantity", 
  "reserved_quantity",
  "minimum_quantity",
  "available_quantity",
  "warehouse_quantity",
  "osd_notes",
  "general_notes",
  "warehouse_name",
  "customer_name",
  "receiving_report_number", // "Inbound Summary"
  "outbound_summary",
  "days_left_to_expire",
  "attachments",
  "filter_field2_name",
];

// Fields to exclude from Inventory Request PDF (Outbound Order)
const INVENTORY_REQUEST_PDF_EXCLUDED_FIELDS = [
  "created_at",
  "updated_at", 
  "last_in",
  "last_out",
  "allocated_quantity",
  "in_cart_quantity", 
  "reserved_quantity",
  "minimum_quantity",
  "available_quantity",
  "warehouse_quantity",
  "rack_locations",
  "osd_notes",
  "general_notes",
  "warehouse_name",
  "customer_name",
  "outbound_summary",
  "days_left_to_expire",
  "attachments",
  "filter_field2_name",
];

// Fields to exclude from Shipment Item PDF (Item Labels)
const SHIPMENT_ITEM_PDF_EXCLUDED_FIELDS = [
  "created_at",
  "updated_at", 
  "last_in",
  "last_out",
  "allocated_quantity",
  "in_cart_quantity", 
  "reserved_quantity",
  "minimum_quantity",
  "available_quantity",
  "warehouse_quantity",
  "rack_locations",
  "osd_notes",
  "general_notes",
  "warehouse_name",
  "customer_name",
  "receiving_report_number", // "Inbound Summary"
  "outbound_summary",
  "days_left_to_expire",
  "attachments",
  "filter_field2_name",
  "s_i_item_notes",
  "s_i_total_pieces",
  // Fields below are already in base template
  "item_id",
  "description",
  "sku_number",
  "photo",
  "available_quantity",
  "expiration_date",
  "expiration_month",
  "expiration_year",
  "expiration_date_month",
  "expiration_month_day_year",
  "s_i_quantity",
];

// Fields to exclude from Shipment Item Move Label PDF
const SHIPMENT_ITEM_MOVE_LABEL_EXCLUDED_FIELDS = [
  "created_at",
  "updated_at",
  "last_in",
  "last_out",
  "allocated_quantity",
  "in_cart_quantity",
  "reserved_quantity",
  "minimum_quantity",
  "available_quantity",
  "warehouse_quantity",
  "rack_locations",
  "osd_notes",
  "general_notes",
  "warehouse_name",
  "customer_name",
  "receiving_report_number",
  "outbound_summary",
  "days_left_to_expire",
  "attachments",
  "filter_field2_name",
  "photo",
];

// Helper function to get mandatory fields for a PDF type
export function getMandatoryFields(pdfType) {
  return MANDATORY_FIELDS[pdfType] || [];
}

export default function usePdfFieldsRules(pdfType, fieldSettings = null) {
  const enabledFields = useEnabledProjectFields(fieldSettings);

  return useMemo(() => {
    // Get exclusion list and hardcoded fields for this PDF type
    const EXCLUDE_MAP = {
      shipment: SHIPMENT_PDF_EXCLUDED_FIELDS,
      shipment_item: SHIPMENT_ITEM_PDF_EXCLUDED_FIELDS,
      inventory_request: INVENTORY_REQUEST_PDF_EXCLUDED_FIELDS,
      shipment_item_move_label: SHIPMENT_ITEM_MOVE_LABEL_EXCLUDED_FIELDS,
    };

    const HARDCODED_MAP = {
      shipment: SHIPMENT_PDF_HARDCODED_FIELDS,
      shipment_item: SHIPMENT_ITEM_PDF_HARDCODED_FIELDS,
      inventory_request: INVENTORY_REQUEST_PDF_HARDCODED_FIELDS,
      shipment_item_move_label: SHIPMENT_ITEM_MOVE_LABEL_HARDCODED_FIELDS,
    };

    const excludeList = EXCLUDE_MAP[pdfType] || [];
    const hardcodedFields = HARDCODED_MAP[pdfType] || [];

    const allFields = [...enabledFields, ...hardcodedFields];

    return allFields.filter(([fieldName]) => {
      // Always exclude fields in the exclusion list
      if (excludeList.includes(fieldName)) return false;

      // Check if this is a hardcoded field for this PDF type
      const isHardcodedField = hardcodedFields.some(([name]) => name === fieldName);

      // For hardcoded fields, apply contextual filtering and include them
      if (isHardcodedField) {
        if (fieldName.startsWith('s_i_')) {
          return pdfType === 'shipment' || pdfType === 'shipment_item' || pdfType === 'shipment_item_move_label';
        }
        if (fieldName.startsWith('i_i_') || fieldName === 'line_item') {
          return pdfType === 'inventory_request';
        }
        // For other hardcoded fields (like truck_number, bol, etc.), include them
        return true;
      }

      // For regular fields, check if enabled in fieldSettings
      if (!fieldSettings) return false;
      return isFieldSettingEnabled(fieldSettings, fieldName);
    });
  }, [enabledFields, pdfType, fieldSettings]);
}
